<template>
  <v-container fluid class="px-6">
    <h1>Settings</h1>
    <SettingsMenu
      v-if="!isInitializing"
      :items="settingOptions"
      :value="selectedTab"
      class="ex-menu-div"
      data-test="setting-menu"
      @onChange="handleTabChange"
      @onTabClick="handleTabClick"
    >
    </SettingsMenu>
    <v-tabs-items
      v-if="!isInitializing"
      v-model="selectedTab"
      class="custom-tabs custom-tabs-shadow"
    >
      <v-tab-item
        :transition="false"
        v-for="(item, index) in settingOptions"
        :key="index"
      >
        <v-card
          :elevation="isFlatCard ? 0 : 1"
          :class="!isFlatCard ? 'elevated-card' : ''"
        >
          <v-card-text
            class="custom-view"
            v-if="
              settingOptions.findIndex((el) => el.title == 'Users') ==
              selectedTab
            "
          >
            <ExCreateEmployeeWizard
              :accessLevel="accessLevel"
              @created="onCreatedUser"
              @onSearch="onFilter"
              @onModalFilter="filteredModalUsers"
              @onFilterRemove="removeFilter"
              :clearSelection="clearSelection"
              :appliedFilters="appliedFilters"
              :valueSearch="filterText"
              :isDialogVisible="isAddDialogVisible"
              @closeAddDialog="isAddDialogVisible = false"
            />
            <DataTable
              class="user-tr mt-5"
              :pagination="paginationModel"
              :headers="headers"
              :data="filteredUsers"
              :currentSort="sortOptions"
              @onRequest="requestData"
              @onSort="handleSort"
              :no-hover-effect="true"
            >
              <template #no-data> </template>
              <template #user="{ row }">
                <UserCard
                  :picture="row.user.data?.avatar"
                  :employeeStatus="
                    row.user.data.employee
                      ? row.user.data.employee?.current_status
                      : row.user.data.user.state_str == 'Operational'
                      ? 'active'
                      : 'invited'
                  "
                  :name="`${row.user.data.user.first_name} ${row.user.data.user.last_name}`"
                  :email="`${row.user.data.user.email}`"
                  :userStatus="row.user.data.user.state"
                  :position="row.user.data.position"
                  :showPosition="true"
                  :hasEmployee="row.user.data.has_employee"
                  :user="{
                    name:
                      row.user.data.user.first_name +
                      ' ' +
                      row.user.data.user.last_name,
                    email: row.user.data.user.email,
                    avatar: row.user.data?.avatar,
                    linkedToEmployee: false,
                    lostAccount:
                      row.user.data.user.state == 'suspended' ||
                      row.user.data.user.state == 'disabled',
                    employeeStatus: row.user.data.user.state,
                    userStatus: row.user.data.user.state_str,
                  }"
                  :employee="{
                    id: row.user.data.employee?.id,
                    name: `${row.user.data.employee?.first_name} ${row.user.data.employee?.last_name}`,
                    avatar: row.user.data.employee?.avatar
                      ? row.user.data.employee?.avatar
                      : row.user.data?.avatar,
                  }"
                ></UserCard>
              </template>
              <template #access="{ row }">
                <div style="display: flex; gap: 10px">
                  <UserPermissionBadge
                    v-for="chipData in row.user.data.accessChip"
                    :key="chipData.appDropdown"
                    :permissionType="chipData.permissionType"
                    :appDropdown="chipData.appDropdown"
                    @onClick="onRemoveAppAccess(chipData.appDropdown, row)"
                  />
                </div>
              </template>
              <template #lastlogin="{ row }">
                <div
                  style="min-width: 260px"
                  v-if="
                    row.user.data.user.last_invited_at &&
                    row.user.data.user.state === 'invited'
                  "
                >
                  <UserActivity
                    :last_invited_at="row.user.data.user.last_invited_at"
                    :date_of_invite="row.user.data.user.date_of_invite"
                  />
                </div>
                <div style="min-width: 260px" v-else>
                  <span v-if="row.user.data.user.state === 'invited'">
                    {{ getUserStateLabel(row.user.data.user.state) }}
                    {{ formatDate(row.user.data.user.date_of_invite) }}
                  </span>
                  <span v-else>
                    {{
                      row.user.data.user.last_login != null
                        ? getUserStateLabel(row.user.data.user.state) +
                          formatLastLoginDate(row.user.data.user.last_login)
                        : ""
                    }}
                  </span>
                </div>
              </template>
              <template #status="{ row }">
                <div>
                  <UserStatusBadge
                    :access="row.user.data.user.state"
                    :status="
                      getUserAccess(
                        row.user.data.user.state,
                        row.user.data.is_suspended
                      )
                    "
                  />
                </div>
              </template>
              <template v-if="flags['users-mdp-344']" #chatter="{ row }">
                <div style="display: flex; justify-content: center">
                  <div class="chatter-icon-container" @click="openChatter(row)">
                    <Icon name="badge_holder_chatter_icon" />
                  </div>
                </div>
              </template>
              <template #actions="{ row }">
                <div>
                  <DropDownMenu
                    v-if="
                      !['closed', 'blocked'].includes(row.user.data.user.state)
                    "
                    @onClick="handleDropdownClick($event, row)"
                    :is-open="false"
                    :dropdown-items="
                      getSettingsUsersItems(row.user.data, appUser)
                    "
                    :translateXOffset="12"
                    :translateYOffset="28"
                  >
                    <template #activator>
                      <div class="icon-container">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="icon-svg"
                        >
                          <path
                            d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                            stroke="#344054"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                            stroke="#344054"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                            stroke="#344054"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                    </template>
                  </DropDownMenu>
                  <!-- Disabled state for closed/blocked users -->
                  <div v-else class="icon-container disabled">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon-svg"
                    >
                      <path
                        d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                        stroke="#98A2B3"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                        stroke="#98A2B3"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                        stroke="#98A2B3"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </template>
            </DataTable>
            <NoRecordFound
              v-if="filteredUsers.length === 0 && userList?.length != 0"
              :title="title"
              :message="title"
              :btnTitle="'User'"
              :subtitle="''"
              class="no-record-found"
              :shouldShowAddAction="true"
              @onAction="isAddDialogVisible = true"
              :showClearAction="userList?.length > 0"
              :clearLabel="clearBtnLabel"
              @onClear="clearSearchFilter()"
              :description="emptyRecordDescription"
              :noBorder="true"
            />
          </v-card-text>
          <v-card-text
            v-if="
              settingOptions.findIndex((el) => el.title == 'Configurations') ==
              selectedTab
            "
          >
            <ConfigurationView
              ref="configurationView"
              @update-shadow="onUpdateShadow"
              :dataSource="availableApps"
              :currencyList="currencyList"
              :countryList="countryList"
              :dropdownCurrencyList="dropdownCurrencyList"
              :dropdownCountryList="dropdownCountryList"
              @onAddCurrency="addCurrency($event)"
              @reloadData="getCurrencyList()"
              @reloadCountryList="getOperatingCountry()"
              @fetchOperatingCountry="getOperatingCountry()"
            />
          </v-card-text>
          <v-card-text
            v-if="
              settingOptions.findIndex((el) => el.title == 'Integrations') ==
              selectedTab
            "
          >
            <AppIntegrationsList />
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <v-dialog v-model="dialogTitle" persistent max-width="480px">
      <div class="pop-up">
        <div class="modal_header">
          <div class="d-flex flex-row">
            <h3 class="text-black-600 mt-5 ml-4">Change Password</h3>
            <CloseIcon className="ml-auto ma-4" @click="reset" />
          </div>
        </div>
        <div class="modal_content mt-4 px-6">
          <v-form ref="password">
            <p class="text-black-500 mb-2">Password</p>
            <v-text-field
              class="input mb-4"
              required
              placeholder="Enter new password"
              outlined
              color="rgba(208, 213, 221, 1)"
              base-color="rgba(208, 213, 221, 1)"
              name="email"
              v-model="password"
              id="password"
              type="password"
              :hide-details="true"
              :rules="validatePassword"
              autofocus
            ></v-text-field>
          </v-form>
        </div>
        <div class="modal_action d-flex justify-end px-6 py-6">
          <MainButton
            className="ml-2"
            :disabled="!canNext"
            @click="onChangePassword()"
            data-test-name="save"
          >
            <span>Save</span>
          </MainButton>
        </div>
      </div>
    </v-dialog>
    <ExActionsPopUp
      v-if="userAccountActionDialogObject?.show ?? false"
      :headline="userAccountActionDialogObject?.title"
      :htmlInfo="userAccountActionDialogObject.description"
      :actionLabel="userAccountActionDialogObject?.actionText"
      :value="userAccountActionDialogObject?.show ?? false"
      :primary="userAccountActionDialogObject.primary ?? false"
      :termination="userAccountActionDialogObject.termination ?? false"
      :destructive="userAccountActionDialogObject.destructive ?? false"
      :avatar="{
        picture: '',
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
      }"
      @onAction="OnActionUserConfirmationDialog"
      @input="OnCloseUserConfirmationDialog"
    />

    <ChangeEmailModel
      v-if="selectedTab === 0 && showChangeEmailDialog"
      :showDialog="showChangeEmailDialog"
      :userList="userList"
      :currentUser="currentSelectedRow"
      @emailUpdated="emailUpdated"
      @closeAddDialog="showChangeEmailDialog = false"
    />

    <RenameUserNameModal
      :showDialog="showRenamePositionDialog"
      :user="{
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
        accountId: String(currentSelectedRow?.account) || '',
        userId: String(currentSelectedRow?.user?.id) || '',
      }"
      @closeDialog="() => (showRenamePositionDialog = false)"
      @reloadData="getUserList()"
    />
    <CancelInviteAdminUserPopup
      :show="showCancelInviteUserPopup"
      :user="{
        id: currentSelectedRow?.user?.id || 0,
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
      }"
      @onCancelInviteAdminUser="cancelInviteAdminUser"
      @onClose="showCancelInviteUserPopup = false"
    />

    <OwnerShipModal
      :dialog="showOnwerShipDialog"
      @onAction="transferOwner($event)"
      @back="() => (showOnwerShipDialog = false)"
      :items="items"
    />
    <EditPermission
      :showDialog="dialogTitle1"
      :currentUser="currentSelectedRow ? currentSelectedRow : {}"
      :availableItems="appAccessTypes"
      :permissions="appPermissions"
      @closeAddDialog="dialogTitle1 = false"
      @onUpdatePermissions="onSaveAppAccess"
      @onUpdateAppPermissions="updateAppPermissions"
    />

    <ChecklistDrawer
      ref="drawerContainer"
      :type="'User'"
      :drawer="openChatterModal.show"
      :typeValue="openChatterModal.userName"
      @closeDrawer="closeUserChatterModal()"
      :chatterList="openChatterModal.chatterData"
      :totalCheckpointChatter="openChatterModal.totalUserChatter"
      :isEmployeeChatter="true"
      :avatar="openChatterModal.userAvatar"
      @requestMoreCheckpointsChatter="requestMoreUserChatter()"
    />
  </v-container>
</template>

<script>
import configService from "../../services/config";
import ExCreateEmployeeWizard from "@components/ExCreateEmployeeWizard.vue";
import SettingsMenu from "@components/SettingsMenu/SettingsMenu.vue";
import DataTable from "@components/DataTable/DataTable.vue";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import CancelInviteAdminUserPopup from "@components/AdminUserActions/CancelInviteAdminUserPopup.vue";
import {
  getListofUser,
  userResendInvite,
  userStateUpdateSuspend,
  userStateUpdateGiveBack,
  changePassword,
  manageAppAccess,
  transferOwnerShip,
  cancelInviteUser,
  getOperatingCurrency,
  getOperatingCountry,
  addOperatingCurrency,
  getCurrencies,
  getCountryList,
  getChatterList,
} from "@/api";
import { transformChatterData } from "@/utils/chatterUtils";
import UserActivity from "@components/SettingView/UserActivity.vue";
import UserCard from "@components/common/UserCard/UserCard.vue";
import UserPermissionBadge from "@components/UserPermissionBadge/UserPermissionBadge.vue";
import UserStatusBadge from "@components/UserStatusBadge/UserStatusBadge.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import CloseIcon from "@components/svgs/CloseIcon.vue";
import Icon from "@components/icons/Icon.vue";
import MainButton from "@components/buttons/MainButton.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import AppIntegrationsList from "@components/AppIntegrationsList/AppIntegrationsList.vue";
import RenameUserNameModal from "@components/RenameUserModal/RenameUserNameModal.vue";
import ChangeEmailModel from "@components/ChangeEmailModal/ChangeEmailModal.vue";
import { createNamespacedHelpers } from "vuex";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import clearStateMixin from "@components/views/mixins/clearStateMixin.js";
import ConfigurationView from "@components/SettingView/SettingConfiguration/ConfigurationView.vue";
import OwnerShipModal from "@components/OwnerShipModal/OwnerShipModal.vue";
import EditPermission from "@components/EditPermission/EditPermissionModal.vue";
import ChecklistDrawer from "@components/Drawer/ChatterDrawer.vue";
const { mapActions } = createNamespacedHelpers("isLoading");
import { getAvailableApps } from "@/api";
export default {
  name: "SettingView",
  data() {
    const accessPermission = JSON.parse(
      localStorage.getItem("accessPermission")
    );
    const accessLevel = accessPermission?.is_owner ? "owner" : "co-owner";

    return {
      payRanges: [
        { id: 1, name: "0-10K", code: "A" },
        { id: 2, name: "10K-20K", code: "B" },
        { id: 3, name: "20K-30K", code: "C" },
      ],
      selectedPayRange: null,
      isFlatCard: false,
      storeFilterKey: "organizationSettings",
      selectedTab: 0,
      isInitializing: true,
      dialog: false,
      email: "",
      accessLevel,
      baseHeaders: [
        {
          text: "User",
          sortable: true,
          value: "user",
        },
        {
          text: "Access",
          sortable: false,
          value: "access",
        },
        {
          text: "Last Seen",
          sortable: false,
          value: "lastlogin",
        },
        {
          text: "Access Status",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          sortable: false,
          value: "chatter",
        },
        {
          text: "",
          sortable: false,
          value: "actions",
        },
      ],
      accountID: "",
      filterText: "",
      userList: [],
      appAccessTypes: [
        {
          id: 1,
          app: {
            name: "Scale Readiness",
            status: "released",
            field: "has_readiness_access",
            icon: "sidebar_readiness",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Readiness",
        },
        {
          id: 2,
          app: {
            name: "Scale Knowledge",
            status: "released",
            field: "has_knowledge_access",
            icon: "sidebar_knowledge",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Knowledge",
        },
        {
          id: 3,
          app: {
            name: "Scale Performance",
            status: "released",
            field: "has_performance_access",
            icon: "sidebar_performance",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Performance",
        },
        {
          id: 4,
          app: {
            name: "Scale Compensation",
            status: "released",
            field: "has_compensation_access",
            icon: "sidebar_compensation",
          },
          permissionType: "App Permission",
          account: this.accountID,
          appDropdown: "Compensation",
        },
      ],
      paginationModel: {
        page: 1,
        total: 0,
        perPage: 10,
      },
      currentSelectedRow: {},
      dialogTitle: false,
      dialogTitle1: false,
      appPermissions: [],
      password: "",
      validatePassword: [(v) => !!v || "Password is required"],
      appliedFilters: ["has access", "invited"],
      clearSelection: "",
      showRenamePositionDialog: false,
      showCancelInviteUserPopup: false,
      showChangeEmailDialog: false,
      isAddDialogVisible: false,
      title: "Users",
      userActions: {
        showResendInvitePopup: false,
        showSuspendPopup: false,
        showGiveBackPopup: false,
      },
      showOnwerShipDialog: false,
      items: [],
      availableApps: {},
      currencyList: [],
      countryList: [],
      dropdownCurrencyList: [],
      dropdownCountryList: [],
      sortOptions: {
        field: null,
        direction: "asc",
      },
      openChatterModal: {
        show: false,
        chatterData: [],
        accessId: null,
        page: 1,
        totalUserChatter: 0,
        userName: "",
        userAvatar: {
          picture: "",
          firstName: "",
          lastName: "",
        },
      },
      isLoadingApps: false,
    };
  },
  mixins: [dropdownMixin, clearStateMixin],
  created() {
    this.accountID = JSON.parse(localStorage.getItem("currentAccountID"));

    // If no view query param, we can show immediately
    if (!this.$route.query.view) {
      this.isInitializing = false;
    }

    this.getUserList();
    this.applySavedFilters();
  },

  async mounted() {
    this.$loader.show();

    await this.$watchFlags({
      show_settingsConfigurationsTab: false,
      show_settingsIntegrationsTab: false,
      show_settingsSubscriptionsTab: false,
      "users-mdp-344": false,
    });

    if (this.$route.query.view) {
      const configIndex = this.settingOptions.findIndex(
        (opt) => opt.title === "Configurations"
      );
      if (configIndex !== -1) {
        this.selectedTab = configIndex;
      }
    }

    await this.getApps();
    await this.getCurrencyList();
    await this.getOperatingCountry(false);
    await this.drowdownCurrencyList();
    await this.fetchDropdownCountryList();

    this.isInitializing = false;

    this.$loader.hide();
  },
  computed: {
    appUser() {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      return currentUser;
    },
    headers() {
      if (this.flags["users-mdp-344"]) {
        return this.baseHeaders;
      } else {
        return this.baseHeaders.filter((header) => header.value !== "chatter");
      }
    },
    settingOptions() {
      return [
        { title: "Users", count: this.paginationModel.total ?? 0 },
        ...(this.flags["show_settingsSubscriptionsTab"]
          ? [{ title: "Subscription", count: 0 }]
          : []),
        ...(this.flags["show_settingsConfigurationsTab"]
          ? [{ title: "Configurations" }]
          : []),
        ...(this.flags["show_settingsIntegrationsTab"]
          ? [{ title: "Integrations", count: 0 }]
          : []),
      ];
    },
    userAccountActionDialogObject() {
      if (this.userActions.showResendInvitePopup) {
        const firstName = this.currentSelectedRow?.user?.first_name || "";
        const lastName = this.currentSelectedRow?.user?.last_name || "";
        return {
          title: "Resend Invite",
          description: `You are about to resend invite to ${firstName} ${lastName}`,
          actionText: "Resend",
          primary: true,
          show: true,
          termination: false,
        };
      } else if (this.userActions.showSuspendPopup) {
        const firstName = this.currentSelectedRow?.user?.first_name || "";
        const lastName = this.currentSelectedRow?.user?.last_name || "";
        return {
          title: "Suspend Access",
          description: `You are about to suspend access for ${firstName} ${lastName}`,
          actionText: "Suspend",
          show: true,
          primary: false,
          termination: false,
          destructive: true,
        };
      } else if (this.userActions.showGiveBackPopup) {
        const firstName = this.currentSelectedRow?.user?.first_name || "";
        const lastName = this.currentSelectedRow?.user?.last_name || "";
        return {
          title: "Give back Access",
          description: `You are about to give back access for ${firstName} ${lastName}`,
          actionText: "Give back Access",
          show: true,
          primary: true,
          termination: false,
          destructive: false,
        };
      }
      return {
        title: "",
        description: "",
        actionText: "",
        show: false,
        primary: false,
        termination: false,
      };
    },
    canNext() {
      if (this.password != "") return true;
      return false;
    },
    filteredUsers: function () {
      let resultArray = [];
      if (!this.userList || !Array.isArray(this.userList)) {
        return resultArray;
      }
      resultArray = this.userList.map((el) => {
        const newEl = {
          user: {
            slot: "user",
            data: el,
          },
          access: {
            slot: "access",
          },
          lastlogin: {
            slot: "lastlogin",
          },
          status: {
            slot: "status",
          },
          ...(this.flags["users-mdp-344"]
            ? {
                chatter: {
                  slot: "chatter",
                },
              }
            : {}),
          actions: {
            slot: "actions",
            data: {},
          },
        };
        return newEl;
      });
      if (this.filterText && this.filterText.length > 0) {
        resultArray = resultArray.filter((el) => {
          const firstName = el.user.data.user.first_name.toUpperCase();
          const lastName = el.user.data.user.last_name.toUpperCase();
          const fullName = `${firstName} ${lastName}`;
          const filterTextUpper = this.filterText.toUpperCase();
          return fullName.includes(filterTextUpper);
        });
      }
      if (this.appliedFilters && this.appliedFilters.length > 0) {
        const userFilters = [
          "invited",
          "has access",
          "suspended",
          "blocked",
          "closed",
          "Is an Employee",
          "Is Not an Employee",
        ];
        const permissionFilters = [
          "Owner",
          "Co-Owner",
          "Readiness",
          "Compensation",
          "Performance",
          "Knowledge",
        ];

        // Apply user status filters
        if (this.appliedFilters.some((val) => userFilters.includes(val))) {
          resultArray = resultArray.filter((el) => {
            return this.appliedFilters.some((el3) => {
              let val = false;
              if (el3 === "Is an Employee") {
                val = el.user.data.has_employee === true;
              } else if (el3 === "Is Not an Employee") {
                val = el.user.data.has_employee === false;
              } else if (el3 === "invited") {
                val =
                  el.user.data.user.state === "invited" &&
                  el.user.data.is_suspended === false;
              } else if (el3 === "has access") {
                val =
                  el.user.data.user.state === "operational" &&
                  el.user.data.is_suspended === false;
              } else if (el3 === "suspended") {
                val = el.user.data.is_suspended === true;
              } else if (el3 === "blocked") {
                val = el.user.data.user.state === "blocked";
              } else if (el3 === "closed") {
                val = el.user.data.user.state === "closed";
              }
              return val;
            });
          });
        }

        // Apply permission filters (radio button based - only one can be selected)
        const permissionFilter = this.appliedFilters.find((filter) =>
          permissionFilters.includes(filter)
        );
        if (permissionFilter) {
          resultArray = resultArray.filter((el) => {
            let val = false;
            if (permissionFilter === "Owner") {
              val = el.user.data.is_owner === true;
            } else if (permissionFilter === "Co-Owner") {
              val = el.user.data.is_co_owner === true;
            } else if (permissionFilter === "Readiness") {
              val = el.user.data.has_readiness_access === true;
            } else if (permissionFilter === "Compensation") {
              val = el.user.data.has_compensation_access === true;
            } else if (permissionFilter === "Performance") {
              val = el.user.data.has_performance_access === true;
            } else if (permissionFilter === "Knowledge") {
              val = el.user.data.has_knowledge_access === true;
            }
            return val;
          });
        }
      }

      // Apply sorting based on sortOptions
      if (this.sortOptions.field) {
        resultArray.sort((a, b) => {
          let valueA, valueB;

          if (this.sortOptions.field === "user") {
            // Full name sorting for the user column
            valueA =
              a.user.data.user.first_name.toUpperCase() +
              " " +
              a.user.data.user.last_name.toUpperCase();
            valueB =
              b.user.data.user.first_name.toUpperCase() +
              " " +
              b.user.data.user.last_name.toUpperCase();
          } else {
            // For other fields, try to get the value from the user data
            valueA = String(
              a.user.data.user[this.sortOptions.field] || ""
            ).toUpperCase();
            valueB = String(
              b.user.data.user[this.sortOptions.field] || ""
            ).toUpperCase();
          }

          if (this.sortOptions.direction === "asc") {
            return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
          } else {
            return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
          }
        });
      } else {
        // Default sorting by full name if no sort field is specified
        resultArray.sort((a, b) => {
          const nameA =
            a.user.data.user.first_name.toUpperCase() +
            " " +
            a.user.data.user.last_name.toUpperCase();
          const nameB =
            b.user.data.user.first_name.toUpperCase() +
            " " +
            b.user.data.user.last_name.toUpperCase();
          if (nameA < nameB) {
            return -1; // a comes before b
          } else if (nameA > nameB) {
            return 1; // b comes before a
          } else {
            return 0; // a and b are equal
          }
        });
      }
      return resultArray;
    },
  },
  watch: {
    filteredUsers(newVal) {
      if (newVal) {
        this.paginationModel.total = newVal.length;
        // Reset to first page when filtered data changes
        this.paginationModel.page = 1;
      }
    },
    // Watch for filter changes to reset pagination
    filterText() {
      this.paginationModel.page = 1;
    },
    appliedFilters() {
      this.paginationModel.page = 1;
    },
  },
  methods: {
    ...mapActions(["setLoadingStatus"]),
    async getApps() {
      // Prevent duplicate simultaneous calls
      if (this.isLoadingApps) {
        return;
      }

      this.isLoadingApps = true;

      try {
        const accountId = localStorage.getItem("currentAccountID");
        const response = await getAvailableApps(accountId);
        localStorage.setItem(
          "currentAccountDetails",
          JSON.stringify(response?.data)
        );

        this.availableApps = response.data;
        this.appAccessTypes = [
          {
            id: 1,
            app: {
              name: "Scale Readiness",
              status: "released",
              field: "has_readiness_access",
              icon: "sidebar_readiness",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Readiness",
            isAppAvailable: this.availableApps.readiness,
          },
          {
            id: 2,
            app: {
              name: "Scale Knowledge",
              status: "released",
              field: "has_knowledge_access",
              icon: "sidebar_knowledge",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Knowledge",
            isAppAvailable: this.availableApps.knowledge,
          },
          {
            id: 3,
            app: {
              name: "Scale Performance",
              status: "released",
              field: "has_performance_access",
              icon: "sidebar_performance",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Performance",
            isAppAvailable: this.availableApps.performance,
          },
          {
            id: 4,
            app: {
              name: "Scale Compensation",
              status: "released",
              field: "has_compensation_access",
              icon: "sidebar_compensation",
            },
            permissionType: "App Permission",
            account: this.accountID,
            appDropdown: "Compensation",
            isAppAvailable: this.availableApps.compensation,
          },
        ];
      } catch (error) {
        console.error("[Settings] Error loading apps:", error);
      } finally {
        this.isLoadingApps = false;
      }
    },
    async emailUpdated() {
      this.formateData();
      this.getUserList();
      this.showChangeEmailDialog = false;
    },
    async cancelInviteAdminUser() {
      this.$loader.setAppLoading(true);
      const userId = this.currentSelectedRow?.user?.id;
      try {
        await cancelInviteUser(userId);
        this.showCancelInviteUserPopup = false;
        this.getUserList();
        this.showCancelInviteUserPopup = false;
      } catch (e) {
        console.error("error: ", e);
      }
      this.$loader.setAppLoading(false);
    },
    getUserAccess(state, isSuspended) {
      if (state == "operational" && isSuspended === false) {
        return "has-access";
      } else if (isSuspended === true) {
        return "suspended";
      } else {
        return state;
      }
    },
    getUserStateLabel(state) {
      return state === "invited" ? "Invited on " : "Last seen on ";
    },
    OnActionUserConfirmationDialog() {
      if (this.userActions.showResendInvitePopup) {
        this.resendInviteAction();
      } else if (this.userActions.showSuspendPopup) {
        this.suspendAccessAction();
      } else if (this.userActions.showGiveBackPopup) {
        this.giveBackAccessAction();
      }
    },
    OnCloseUserConfirmationDialog() {
      if (this.userActions.showResendInvitePopup) {
        this.userActions.showResendInvitePopup = false;
      } else if (this.userActions.showSuspendPopup) {
        this.userActions.showSuspendPopup = false;
      } else if (this.userActions.showGiveBackPopup) {
        this.userActions.showGiveBackPopup = false;
      }
    },
    formatDate(date) {
      const originalDate = new Date(date);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[originalDate.getMonth()];
      const year = originalDate.getFullYear();
      const day = String(originalDate.getDate()).padStart(2, "0");

      // Pad hours and minutes with leading zeros
      const hours = String(originalDate.getHours()).padStart(2, "0");
      const minutes = String(originalDate.getMinutes()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    },
    formatLastLoginDate(date) {
      const originalDate = new Date(date);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[originalDate.getMonth()];
      const year = originalDate.getFullYear();
      const day = String(originalDate.getDate()).padStart(2, "0");

      // Use 24-hour format
      const hours = String(originalDate.getHours()).padStart(2, "0");
      const minutes = String(originalDate.getMinutes()).padStart(2, "0");

      return `${day}-${month}-${year} ${hours}:${minutes}`;
    },
    async onCreatedUser(user) {
      console.log(user);
      await this.loadUsers();
    },
    onUpdateShadow(val) {
      this.isFlatCard = val;
    },
    handleTabChange(tabIndex) {
      // Find the Configurations tab index dynamically
      const configTabIndex = this.settingOptions.findIndex(
        (opt) => opt.title === "Configurations"
      );

      // If clicking on Configurations tab and we're already on it, call goBack
      if (tabIndex === configTabIndex && this.selectedTab === configTabIndex) {
        // Same tab clicked - trigger goBack
        this.$nextTick(() => {
          setTimeout(() => {
            let configView = this.$refs.configurationView;

            // Handle case where ref is an array (when component is in v-for)
            if (Array.isArray(configView)) {
              // Get the first component or find the one that's currently visible
              configView =
                configView.find(
                  (component) =>
                    component &&
                    component.$el &&
                    component.$el.offsetParent !== null
                ) || configView[0];
            }

            if (configView && typeof configView.onGoBack === "function") {
              configView.onGoBack();
              return; // Don't proceed with normal tab change
            } else {
              // Fallback: directly perform the goBack actions
              if (this.$route.query.view) {
                const query = { ...this.$route.query };
                delete query.view;
                // Use catch to handle the case where navigation might be redundant
                this.$router.replace({ query }).catch((err) => {
                  // Ignore navigation duplicated errors
                  if (err.name !== "NavigationDuplicated") {
                    throw err;
                  }
                });
              }
              this.isFlatCard = false;
              return; // Don't proceed with normal tab change
            }
          }, 150);
        });
        return; // Exit early to prevent normal tab change
      }

      // Normal tab change
      this.isFlatCard = false;
      this.selectedTab = tabIndex;
      if (this.$route.query.view) {
        const query = { ...this.$route.query };
        delete query.view;
        this.$router.replace({ query });
      }
    },
    handleTabClick(tabIndex) {
      const configTabIndex = this.settingOptions.findIndex(
        (opt) => opt.title === "Configurations"
      );

      console.log(
        "handleTabClick - tabIndex:",
        tabIndex,
        "configTabIndex:",
        configTabIndex,
        "selectedTab:",
        this.selectedTab
      );

      // If clicking on Configurations tab and we're already on it, call goBack
      if (tabIndex === configTabIndex && this.selectedTab === configTabIndex) {
        console.log("Same Configurations tab clicked, calling goBack");

        // Wait a bit longer to ensure component is rendered
        this.$nextTick(() => {
          setTimeout(() => {
            let configView = this.$refs.configurationView;
            console.log("ConfigView ref:", configView);
            if (Array.isArray(configView)) {
              configView =
                configView.find(
                  (component) =>
                    component &&
                    component.$el &&
                    component.$el.offsetParent !== null
                ) || configView[0];
            }

            if (configView && typeof configView.onGoBack === "function") {
              configView.onGoBack();
            } else {
              if (this.$route.query.view) {
                const query = { ...this.$route.query };
                delete query.view;
                this.$router.replace({ query });
              }
              this.isFlatCard = false;
            }
          }, 150);
        });
      }
    },
    applySavedFilters() {
      this.$store.dispatch("filterStore/loadFilters", this.storeFilterKey);
      const savedFilters = this.$store.state.filterStore.filters;
      if (savedFilters && savedFilters.length > 0) {
        this.appliedFilters = savedFilters;
      }
    },
    resendInviteAction() {
      this.setLoadingStatus(true);
      if (this.currentSelectedRow?.user?.id) {
        const obj = {
          user: this.currentSelectedRow?.user.id,
          account: this.accountID ?? this.currentSelectedRow.account,
        };
        userResendInvite(this.currentSelectedRow.user.id, obj)
          .then(() => {
            //
          })
          .catch((err) => {
            console.error("error", err);
          })
          .finally(() => {
            this.getUserList();
            this.setLoadingStatus(false);
            this.userActions.showResendInvitePopup = false;
          });
      } else {
        this.setLoadingStatus(false);
        this.userActions.showResendInvitePopup = false;
      }
    },
    suspendAccessAction() {
      this.setLoadingStatus(true);
      if (this.currentSelectedRow?.user?.id) {
        let obj = {
          email: this.currentSelectedRow.user.email,
          state: "suspended",
        };
        userStateUpdateSuspend(this.accountID, this.currentSelectedRow.id, obj)
          .then(() => {
            this.currentSelectedRow.user.state = "suspended";
            this.formateData();
            this.getUserList();
          })
          .catch((err) => {
            if (err.response?.data?.detail) {
              this.$toast.error(err.response.data.detail);
            } else {
              this.$toast.error("something went wrong, please try again.");
            }
          })
          .finally(() => {
            this.setLoadingStatus(false);
            this.userActions.showSuspendPopup = false;
          });
      } else {
        this.setLoadingStatus(false);
        this.userActions.showSuspendPopup = false;
      }
    },
    giveBackAccessAction() {
      this.setLoadingStatus(true);
      if (this.currentSelectedRow?.user?.id) {
        let obj = {
          email: this.currentSelectedRow.user.email,
          state: "invited",
        };
        userStateUpdateGiveBack(this.accountID, this.currentSelectedRow.id, obj)
          .then(() => {
            this.currentSelectedRow.user.state = "invited";
            this.formateData();
            this.getUserList();
          })
          .catch((err) => {
            if (err.response?.data?.detail) {
              this.$toast.error(err.response.data.detail);
            } else {
              this.$toast.error("something went wrong, please try again.");
            }
          })
          .finally(() => {
            this.setLoadingStatus(false);
            this.userActions.showGiveBackPopup = false;
          });
      } else {
        this.setLoadingStatus(false);
        this.userActions.showGiveBackPopup = false;
      }
    },
    getUserList() {
      // this.setLoadingStatus(true);
      getListofUser(this.accountID)
        .then((res) => {
          this.userList = res.data.results;
          this.formateData();
          if (res.data && res.data.count) {
            this.paginationModel.total = res.data.count;
          }
        })
        .catch((error) => {
          this.userList = [];
          console.log("error: ", error);
        })
        .finally(() => {
          // this.setLoadingStatus(false);
        });
    },
    async requestData(data) {
      if (data && data.page) {
        this.paginationModel.page = data.page;
      }
    },
    async loadUsers(pageNo = 1) {
      try {
        const { data } = await getListofUser(this.accountID, pageNo, 10);
        if (data && data.count) {
          this.paginationModel.total = data.count;
        }
        if (data && data.results) {
          this.userList = data.results;
          this.formateData();
        }
      } catch (error) {
        // Handle 500 error by showing no records
        console.error("Failed to load users:", error);
        this.userList = [];
        this.paginationModel.total = 0;
      }
    },
    formateData() {
      this.userList.forEach((element) => {
        element.user.full_name =
          element.user.first_name + " " + element.user.last_name;
        element.accessChip = [];
        if (element.has_access) {
          if (element.is_owner) {
            element.accessChip.push({
              permissionType: "Owner",
              appDropdown: "Owner",
              IsEnabled: true,
            });
          } else if (element.is_co_owner) {
            element.accessChip.push({
              permissionType: "Co-Owner",
              appDropdown: "Co-Owner",
              IsEnabled: true,
            });
          } else {
            this.appAccessTypes.forEach((access) => {
              if (element[access.app.field]) {
                element.accessChip.push(access);
              }
            });
          }
        }
        element.actionMenu = [];
        if (element.user.state == "invited") {
          element.actionMenu.push({
            name: "Resend Invite",
            value: "resend_invite",
            icon: "icons/dropdown/resend.svg",
            order: 1,
            disabled: false,
          });
        } else if (element.user.state == "draft") {
          element.actionMenu.push({
            name: "Activate",
            value: "activate",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        } else if (element.user.state == "active") {
          element.actionMenu.push({
            name: "Suspend",
            value: "suspend",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        } else if (element.user.state == "suspended") {
          element.actionMenu.push({
            name: "Re-Activate",
            value: "reactivate",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        }
        element.actionMenu.push({
          name: "Change Password",
          value: "change_password",
          icon: "edit_employee.svg",
          order: 1,
          disabled: false,
        });
        if (
          !element.is_owner &&
          !element.is_co_owner &&
          !element.is_suspended
        ) {
          element.actionMenu.push({
            name: "Manage Apps Access",
            value: "manage_apps",
            icon: "edit_employee.svg",
            order: 1,
            disabled: false,
          });
        }
        if (!element.is_owner && !element.is_suspended) {
          element.actionMenu.push({
            name: "Suspend",
            value: "suspend",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
            negative: true,
          });
        }
        if (!element.is_owner && element.is_suspended) {
          element.actionMenu.push({
            name: "Give back access",
            value: "give_back_access",
            icon: "changeStatus.svg",
            order: 1,
            disabled: false,
          });
        }
      });
    },
    handleDropdownClick(data, row) {
      console.log("🚀 ~ handleDropdownClick ~ data:", data);
      this.currentSelectedRow = this.userList.find(
        (x) => x.id === row.user.data.id
      );
      if (data.value == "resend_invite") {
        this.userActions.showResendInvitePopup = true;
      } else if (data.value == "suspend") {
        this.userActions.showSuspendPopup = true;
      } else if (data.value == "give_back_access") {
        this.userActions.showGiveBackPopup = true;
      } else if (data.value == "change_permissions_action") {
        this.appPermissions = [];
        this.dialogTitle1 = true;
        this.dialogTitle = false;
        this.appAccessTypes.forEach((access) => {
          if (row.user.data[access.app.field]) {
            this.appPermissions.push(access.id);
          }
        });
      } else if (data.value == "rename_action") {
        this.showRenamePositionDialog = true;
        this.dialogTitle = false;
      } else if (data.value == "change_email_action") {
        this.showChangeEmailDialog = true;
        this.dialogTitle = false;
      } else if (data.value == "transfer_ownership_action") {
        this.showOnwerShipDialog = true;
        this.dialogTitle = false;
        this.items = [];
        this.userList.forEach((element) => {
          if (
            element.user.state !== "closed" &&
            element.user.state !== "blocked"
          ) {
            if (element.is_owner === false && element.is_suspended === false) {
              this.items.push({
                id: element.user.id,
                fullName: element.user.full_name,
                email: element.user.email,
              });
            }
          }
        });
      } else if (data.value === "cancel_invite_action") {
        this.showCancelInviteUserPopup = true;
      }
    },
    transferOwner(event) {
      this.setLoadingStatus(true);
      const obj = {
        email: event.user.email,
      };
      transferOwnerShip(this.accountID, obj)
        .then(() => {
          this.loadUsers();
        })
        .catch((err) => {
          console.error("error", err);
        })
        .finally(() => {
          this.setLoadingStatus(false);
          this.showOnwerShipDialog = false;
        });
    },
    onChangePassword() {
      if (this.$refs.password.validate()) {
        this.setLoadingStatus(true);
        changePassword(this.currentSelectedRow.user.id, {
          password: this.password,
        })
          .then(() => {
            // this.$refs.password.reset();
          })
          .catch((err) => {
            if (err.response?.data?.detail) {
              this.$toast.error(err.response.data.detail);
            } else {
              this.$toast.error("something went wrong, please try again.");
            }
          })
          .finally(() => {
            this.$refs.password.reset();
            this.dialogTitle = false;
            this.setLoadingStatus(false);
          });
      }
    },
    updateAppPermissions(val) {
      this.appPermissions = val;
    },
    onSaveAppAccess() {
      this.$loader.setAppLoading(true);
      const obj = {};
      this.appAccessTypes.forEach((item) => {
        obj[item.app.field] = this.appPermissions.includes(item.id);
      });
      manageAppAccess(this.accountID, this.currentSelectedRow.user.id, obj)
        .then(() => {
          this.appAccessTypes.forEach((access) => {
            this.currentSelectedRow[access.app.field] =
              this.appPermissions.includes(access.id);
          });
          this.formateData();
        })
        .catch((err) => {
          console.log("err: ", err);
          this.$toast.error("something went wrong, please try again.");
        })
        .finally(() => {
          this.dialogTitle1 = false;
          this.$loader.setAppLoading(false);
        });
    },
    onRemoveAppAccess(access, row) {
      this.currentSelectedRow = this.userList.find(
        (x) => x.id === row.user.data.id
      );
      this.setLoadingStatus(true);
      const obj = {};
      switch (access) {
        case "Proficiency":
          obj.has_readiness_access =
            this.appPermissions.includes("Proficiency");
          break;
        case "Knowledge":
          obj.has_knowledge_access = this.appPermissions.includes("Knowledge");
          break;
        case "Performance":
          obj.has_performance_access =
            this.appPermissions.includes("Performance");
          break;
        case "Compensation":
          obj.has_compensation_access =
            this.appPermissions.includes("Compensation");
          break;
        case "Employee Portal":
          obj.has_employee_portal_access =
            this.appPermissions.includes("Employee Portal");
          break;
        default:
          break;
      }
      manageAppAccess(this.accountID, this.currentSelectedRow.user.id, obj)
        .then(() => {
          this.appAccessTypes.forEach((access) => {
            this.currentSelectedRow[access.app.field] =
              this.appPermissions.includes(access.id);
          });
          this.loadUsers();
          this.formateData();
        })
        .catch((err) => {
          console.log("err: ", err);
          this.$toast.error("something went wrong, please try again.");
        })
        .finally(() => {
          this.dialogTitle1 = false;
          this.setLoadingStatus(false);
        });
    },
    reset() {
      this.dialogTitle = false;
      this.dialogTitle1 = false;
      this.accountPermission = {
        is_co_owner: true,
        has_access: true,
        has_readiness_access: false,
        has_knowledge_access: false,
        has_performance_access: false,
        has_compensation_access: false,
        has_employee_portal_access: true,
      };
    },
    onFilter(data) {
      this.filterText = data;
      // Reset pagination to first page when filtering
      this.paginationModel.page = 1;
      // Emit filter event to child components if they need to handle filtering
      this.$nextTick(() => {
        if (this.selectedTab === 2) {
          // Configuration tab
          // Give child components time to initialize before filtering
          setTimeout(() => {
            this.$emit("onFilterApplied", data);
          }, 100);
        }
      });
    },
    removeFilter(removedFilter) {
      this.$store.dispatch("filterStore/updateFilters", {
        key: this.storeFilterKey,
        filters: this.appliedFilters,
      });
      this.clearSelection = removedFilter;
      // Reset pagination to first page when removing a filter
      this.paginationModel.page = 1;
    },
    filteredModalUsers(filters) {
      const newArray = filters
        .map((item) => {
          if (item.value !== null) {
            if (typeof item === "string") {
              return item;
            } else if (
              typeof item === "object" &&
              // eslint-disable-next-line no-prototype-builtins
              item.hasOwnProperty("value")
            ) {
              return item.value;
            }
          }
        })
        .filter(Boolean);
      this.appliedFilters = newArray;
      // Reset pagination to first page when filters are applied
      this.paginationModel.page = 1;
      this.$store.dispatch("filterStore/updateFilters", {
        key: this.storeFilterKey,
        filters: this.appliedFilters,
      });
    },
    setUserStatus(user) {
      if (user.state == "") {
        return "has-access";
      } else {
        return "has-access";
      }
    },
    clearSearchFilter() {
      if (this.appliedFilters?.length > 0) {
        this.appliedFilters = [];
        this.filteredModalUsers(this.appliedFilters);
      }
      if (this.filterText?.length > 0) {
        this.filterText = "";
      }
      // Reset pagination to first page when clearing filters
      this.paginationModel.page = 1;
    },
    toggleAppSelect(appId) {
      if (this.appPermissions.includes(appId)) {
        this.appPermissions = [
          ...this.appPermissions.filter(
            (appInstallId) => appInstallId != appId
          ),
        ];
      } else {
        this.appPermissions = [...this.selectedApps, appId];
      }
    },
    onPayRangeSelected(selectedItem) {
      this.selectedPayRange = selectedItem;
      console.log("Selected Pay Range:", selectedItem);
    },
    onPayRangeCleared() {
      this.selectedPayRange = null;
      console.log("Pay range selection cleared");
    },
    async drowdownCurrencyList() {
      await getCurrencies()
        .then((res) => {
          res.data.results.forEach((element) => {
            this.dropdownCurrencyList.push({
              id: element.id,
              name: element.name,
              code: element.code,
              symbol: element.symbol,
              countries: element.countries
                ?.map((country) => ({
                  ...country,
                  flag_icon: country.flag_icon
                    ? `${configService.get("baseURL")}${country.flag_icon}`
                    : null,
                }))
                .sort((a, b) => b.name.localeCompare(a.name)),
              icon: `${element.countries[0].flag_icon}`,
            });
          });
        })
        .catch((error) => {
          this.dropdownCurrencyList = [];
          console.log("error: ", error);
        });
    },
    async fetchDropdownCountryList() {
      await getCountryList(this.accountID)
        .then((res) => {
          if (res.data && res.data.results && Array.isArray(res.data.results)) {
            this.dropdownCountryList = res.data.results.map(
              (country, index) => {
                if (!country.id) {
                  console.warn(
                    `Country at index ${index} is missing id property:`,
                    country
                  );
                }
                return {
                  id: country.id,
                  name: country.name,
                  icon: country.flag_icon
                    ? `${configService.get("baseURL")}${country.flag_icon}`
                    : null,
                };
              }
            );
          } else {
            console.error(
              "Invalid API response structure for country list:",
              res.data
            );
            this.dropdownCountryList = [];
          }
        })
        .catch((error) => {
          this.dropdownCountryList = [];
          console.log("error: ", error);
        });
    },
    async getCurrencyList() {
      // this.setLoadingStatus(true);
      try {
        const res = await getOperatingCurrency(this.accountID);
        this.currencyList = res.data.results
          .sort((a, b) => a.currency_name.localeCompare(b.currency_name))
          .map((currency, index) => ({
            ...currency,
            id: index + 1,
            countries: currency.countries.map((country) => ({
              ...country,
              flag_icon: `${configService.get("baseURL")}${country.flag_icon}`,
            })),
          }));
      } catch (error) {
        this.currencyList = [];
        console.error("Error fetching currency list:", error);
      } finally {
        // this.setLoadingStatus(false);
      }
    },
    async getOperatingCountry(showLoader = true) {
      if (showLoader) {
        this.$loader.show();
      }
      await getOperatingCountry(this.accountID)
        .then((res) => {
          this.countryList = res.data.results;
        })
        .catch((error) => {
          this.countryList = [];
          console.log("error: ", error);
        })
        .finally(() => {
          if (showLoader) {
            this.$loader.hide();
          }
        });
    },
    async addCurrency(event) {
      this.$loader.setAppLoading(true);
      this.$loader.show();
      const payload = {
        status: "draft",
        exchange_rate_to_reference: "1.00",
        currency: event.id,
      };
      await addOperatingCurrency(this.accountID, payload)
        .then(async () => {
          await this.getCurrencyList();
        })
        .catch((error) => {
          console.log("error: ", error);
        })
        .finally(() => {
          this.$loader.setAppLoading(false);
          this.$loader.hide();
        });
    },
    handleSort(sortData) {
      this.sortOptions = {
        field: sortData.field,
        direction: sortData.direction,
      };
      // Reset pagination to first page when sorting
      this.paginationModel.page = 1;
    },
    async openChatter(row) {
      try {
        this.openChatterModal.show = true;
        this.openChatterModal.chatterData = [];
        this.openChatterModal.accessId = row.user.data.id;
        this.openChatterModal.page = 1;
        this.openChatterModal.userName = `${row.user.data.user.first_name} ${row.user.data.user.last_name}`;
        this.openChatterModal.userAvatar = {
          picture: row.user.data?.avatar || "",
          firstName: row.user.data.user.first_name || "",
          lastName: row.user.data.user.last_name || "",
        };
        await this.getUserChatter();
      } catch (error) {
        console.error("Error opening chatter:", error);
      }
    },
    async getUserChatter() {
      try {
        const response = await getChatterList(
          this.accountID,
          this.openChatterModal.accessId,
          "user",
          this.openChatterModal.page
        );
        if (response) {
          const chatterItems = transformChatterData(response.data.results);
          this.openChatterModal.chatterData.push(...chatterItems);
          this.openChatterModal.totalUserChatter = response.data.count;
        }
      } catch (error) {
        console.log("Error fetching user chatter:", error);
      }
    },
    closeUserChatterModal() {
      this.openChatterModal.show = false;
      this.openChatterModal.chatterData = [];
      this.openChatterModal.accessId = null;
      this.openChatterModal.page = 1;
      this.openChatterModal.totalUserChatter = 0;
      this.openChatterModal.userName = "";
      this.openChatterModal.userAvatar = {
        picture: "",
        firstName: "",
        lastName: "",
      };
    },
    async requestMoreUserChatter() {
      this.openChatterModal.page += 1;
      await this.getUserChatter();
    },
  },
  components: {
    CancelInviteAdminUserPopup,
    EditPermission,
    ExCreateEmployeeWizard,
    SettingsMenu,
    DataTable,
    UserCard,
    UserPermissionBadge,
    UserActivity,
    UserStatusBadge,
    DropDownMenu,
    CloseIcon,
    Icon,
    MainButton,
    ExActionsPopUp,
    AppIntegrationsList,
    RenameUserNameModal,
    NoRecordFound,
    ConfigurationView,
    OwnerShipModal,
    ChangeEmailModel,
    ChecklistDrawer,
  },
};
</script>
<style scoped>
.with-employee {
  width: 100%;
}

.chatter-icon-container {
  padding: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.chatter-icon-container:hover {
  background-color: #f2edff;
  box-shadow: 0 4px 4px 0 hsla(0, 0%, 40%, 0.1);
  transform: translateY(-1px);
}
</style>
<style scoped>
.button-next {
  width: 87px;
  height: 44px;
  padding: 10px 16px 10px 16px;
  border-radius: 8px;
  border: solid 1px rgba(234, 236, 240, 1);
  background-color: rgba(242, 244, 247, 1);
  color: rgba(152, 162, 179, 1);
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
}

.text-black-600 {
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  color: rgba(16, 24, 40, 1);
}

.text-black-500 {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: rgba(52, 64, 84, 1);
}
.v-text-field__slot {
  font-family: Inter;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: rgba(102, 112, 133, 1);
}
.input {
  border-radius: 8px;
  margin: 0;
}

.pop-up {
  width: 480px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 1);
  position: relative;
}
.modal_header {
  z-index: 1;
  position: relative;
}
.modal_content {
  z-index: 1;
  position: relative;
}
.font-weight-500 {
  font-weight: 500;
}
.font-weight-400 {
  font-weight: 400;
}
.font-size-14 {
  font-size: 14px;
}
.text-disabled {
  color: rgba(102, 112, 133, 1);
}

.text-gray-dark {
  color: #666666;
}
.app-selector {
  border: 2px solid rgba(127, 86, 217, 1);
  cursor: pointer;
}
.v-input--selection-controls {
  padding-top: 0;
}
.v-application--is-ltr .v-input--selection-controls__input {
  margin: 0px;
}
.v-input--selection-controls__input .v-input__slot .v-label {
  padding-left: 1px !important;
}
.v-input--selection-controls__input .v-input__slot .theme--light .v-label {
  color: black;
  font-size: 14px;
}
</style>

<style>
.pop-up .v-input__slot {
  min-height: 44px !important;
}
.storybook-status-badge--suspended .custom-bedge.v-badge--dot .v-badge__badge {
  background-color: #b42318 !important;
}
.owner-badge-list {
  height: 60px !important;
}

.elevated-card.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03) !important;
}

.st-setting
  .container--fluid
  .v-item-group
  .v-window-item
  .v-sheet.v-card:not(.v-sheet--outlined) {
  margin: 20px;
}

.st-setting .horizontal-tab {
  width: 96%;
  margin-left: 20px;
}
.st-setting h1 {
  margin-left: 20px;
}

.icon-container.disabled {
  cursor: not-allowed;
  opacity: 0.5;
  top: 15px;
  cursor: not-allowed;
  left: -8px;
}

::v-deep(.v-list-item) {
  margin-bottom: 300px !important;
}
</style>
<style>
.storybook-setting .employee_availability_icon .icon-container {
  position: relative !important;
  top: 15px !important;
}
</style>
